import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  FiStar, FiRefreshCw, FiPlus, FiTrash2, FiSettings, 
  FiClock, FiUser, FiMessageSquare, FiCheck, FiAlertCircle
} from 'react-icons/fi';
import { toast } from 'sonner';

const API_URL = getApiUrl();

export default function GoogleReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [reviewsInfo, setReviewsInfo] = useState({ total: 0, average: 0 });
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showEditStats, setShowEditStats] = useState(false);
  const [editStats, setEditStats] = useState({ total_reviews: 0, average_rating: 5 });
  const [config, setConfig] = useState({ place_url: '', place_name: '', place_id: '' });
  const [newReview, setNewReview] = useState({
    author_name: '',
    rating: 5,
    text: '',
    relative_time: 'Récemment'
  });

  useEffect(() => {
    loadReviews();
    loadConfig();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/google-reviews/`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
        setReviewsInfo({
          total: data.total_reviews || data.reviews?.length || 0,
          average: data.average_rating || 0
        });
        setLastSync(data.last_sync);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
      toast.error('Erreur lors du chargement des avis');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/google-reviews/config`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      toast.info('Synchronisation en cours...');
      
      const response = await fetch(`${API_URL}/api/google-reviews/sync-now`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Synchronisation terminée: ${data.reviews_count || 0} avis`);
        loadReviews();
      } else {
        toast.error('Erreur lors de la synchronisation');
      }
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleInitDefault = async () => {
    try {
      setSyncing(true);
      const response = await fetch(`${API_URL}/api/google-reviews/init-default`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        loadReviews();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'initialisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleAddReview = async () => {
    if (!newReview.author_name || !newReview.text) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/google-reviews/add-manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newReview,
          id: `manual-${Date.now()}`
        })
      });

      if (response.ok) {
        toast.success('Avis ajouté avec succès');
        setNewReview({ author_name: '', rating: 5, text: '', relative_time: 'Récemment' });
        setShowAddForm(false);
        loadReviews();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout de l\'avis');
    }
  };

  const handleSaveConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/google-reviews/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Configuration sauvegardée');
        setShowConfig(false);
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleSaveStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/google-reviews/update-place-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editStats)
      });

      if (response.ok) {
        toast.success('Statistiques mises à jour');
        setShowEditStats(false);
        loadReviews();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleClearReviews = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer tous les avis ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/google-reviews/clear`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Avis supprimés');
        loadReviews();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Jamais';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Avis Google</h1>
          <p className="text-slate-500">Gérez et synchronisez vos avis Google</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setShowConfig(!showConfig)}
          >
            <FiSettings className="mr-2" />
            Configuration
          </Button>
          <Button
            variant="outline"
            onClick={handleInitDefault}
            disabled={syncing}
          >
            <FiPlus className="mr-2" />
            Avis par défaut
          </Button>
          <Button
            onClick={handleSync}
            disabled={syncing}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <FiRefreshCw className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Synchronisation...' : 'Synchroniser'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className="cursor-pointer hover:border-yellow-300 transition-colors"
          onClick={() => {
            setEditStats({ ...editStats, average_rating: reviewsInfo.average, total_reviews: reviewsInfo.total });
            setShowEditStats(true);
          }}
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <FiStar className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Note moyenne</p>
                <p className="text-2xl font-bold">{reviewsInfo.average.toFixed(1)}/5</p>
                <p className="text-xs text-blue-500 mt-1">Cliquer pour modifier</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:border-blue-300 transition-colors"
          onClick={() => {
            setEditStats({ ...editStats, average_rating: reviewsInfo.average, total_reviews: reviewsInfo.total });
            setShowEditStats(true);
          }}
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FiMessageSquare className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total avis</p>
                <p className="text-2xl font-bold">{reviewsInfo.total}</p>
                <p className="text-xs text-blue-500 mt-1">Cliquer pour modifier</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <FiCheck className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avis affichés</p>
                <p className="text-2xl font-bold">{reviews.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <FiClock className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Dernière sync</p>
                <p className="text-sm font-medium">{formatDate(lastSync)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FiAlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800 font-medium">Synchronisation automatique</p>
            <p className="text-sm text-blue-600">
              Les avis Google sont synchronisés automatiquement tous les jours à 3h00 du matin. 
              Vous pouvez également déclencher une synchronisation manuelle à tout moment.
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Panel */}
      {showConfig && (
        <Card>
          <CardHeader>
            <CardTitle>Configuration du scraping</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>URL Google Maps</Label>
              <Input
                value={config.place_url || ''}
                onChange={(e) => setConfig({ ...config, place_url: e.target.value })}
                placeholder="https://www.google.com/maps/place/..."
              />
              <p className="text-xs text-slate-500 mt-1">
                Copiez l'URL complète de votre établissement sur Google Maps
              </p>
            </div>
            <div>
              <Label>Nom de l'établissement</Label>
              <Input
                value={config.place_name || ''}
                onChange={(e) => setConfig({ ...config, place_name: e.target.value })}
                placeholder="Just A Smile Events"
              />
            </div>
            <div>
              <Label>Place ID (optionnel)</Label>
              <Input
                value={config.place_id || ''}
                onChange={(e) => setConfig({ ...config, place_id: e.target.value })}
                placeholder="ChIJ..."
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveConfig}>Sauvegarder</Button>
              <Button variant="outline" onClick={() => setShowConfig(false)}>Annuler</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Stats Modal */}
      {showEditStats && (
        <Card>
          <CardHeader>
            <CardTitle>Modifier les statistiques Google</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Modifiez le nombre total d'avis et la note moyenne affichés sur votre site. 
              Ces valeurs doivent correspondre à celles de votre page Google My Business.
            </p>
            <div>
              <Label>Nombre total d'avis sur Google</Label>
              <Input
                type="number"
                min="0"
                value={editStats.total_reviews}
                onChange={(e) => setEditStats({ ...editStats, total_reviews: parseInt(e.target.value) || 0 })}
                placeholder="127"
              />
              <p className="text-xs text-slate-500 mt-1">
                Vérifiez sur votre page Google Maps pour le nombre exact
              </p>
            </div>
            <div>
              <Label>Note moyenne</Label>
              <Input
                type="number"
                min="1"
                max="5"
                step="0.1"
                value={editStats.average_rating}
                onChange={(e) => setEditStats({ ...editStats, average_rating: parseFloat(e.target.value) || 5 })}
                placeholder="4.9"
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSaveStats}>
                Sauvegarder
              </Button>
              <Button variant="outline" onClick={() => setShowEditStats(false)}>
                Annuler
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Review Form */}
      {showAddForm ? (
        <Card>
          <CardHeader>
            <CardTitle>Ajouter un avis manuellement</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Nom de l'auteur *</Label>
              <Input
                value={newReview.author_name}
                onChange={(e) => setNewReview({ ...newReview, author_name: e.target.value })}
                placeholder="Marie Dupont"
              />
            </div>
            <div>
              <Label>Note *</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setNewReview({ ...newReview, rating: n })}
                    className={`p-2 rounded transition-colors ${
                      newReview.rating >= n ? 'text-yellow-500' : 'text-slate-300'
                    }`}
                  >
                    <FiStar className={`h-6 w-6 ${newReview.rating >= n ? 'fill-current' : ''}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Texte de l'avis *</Label>
              <Textarea
                value={newReview.text}
                onChange={(e) => setNewReview({ ...newReview, text: e.target.value })}
                placeholder="Excellente prestation..."
                rows={4}
              />
            </div>
            <div>
              <Label>Date relative</Label>
              <Input
                value={newReview.relative_time}
                onChange={(e) => setNewReview({ ...newReview, relative_time: e.target.value })}
                placeholder="il y a 2 semaines"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddReview}>Ajouter l'avis</Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Annuler</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button variant="outline" onClick={() => setShowAddForm(true)}>
          <FiPlus className="mr-2" />
          Ajouter un avis manuellement
        </Button>
      )}

      {/* Reviews List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Avis ({reviews.length})</CardTitle>
          {reviews.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-red-600 hover:text-red-700"
              onClick={handleClearReviews}
            >
              <FiTrash2 className="mr-2 h-4 w-4" />
              Tout supprimer
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <FiRefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-400" />
              <p className="text-slate-500 mt-2">Chargement...</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-8">
              <FiMessageSquare className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Aucun avis pour le moment</p>
              <p className="text-sm text-slate-400">Cliquez sur "Synchroniser" ou "Avis par défaut"</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review, idx) => (
                <motion.div
                  key={review.id || idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                      {review.author_name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="font-medium text-slate-900">{review.author_name}</p>
                        <span className="text-xs text-slate-400">{review.relative_time}</span>
                      </div>
                      <div className="flex gap-0.5 mb-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <FiStar
                            key={i}
                            className={`h-4 w-4 ${
                              i <= review.rating ? 'text-yellow-500 fill-current' : 'text-slate-300'
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-sm text-slate-600">{review.text}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
